import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Heart, Sparkles, Sun, Moon, User, Users, Eye, FileText, Check, Clock, Shield, Target, Lightbulb, MessageCircle, Gem, Zap, TrendingUp, AlertTriangle, Calendar, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { ServicePurchaseSection } from '../components/ServicePurchaseSection';
import { AdSlot } from '../components/ads/AdSlot';
interface CompatibilityPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-23038">
      {children}
    </motion.section>;
}
export function CompatibilityPage({
  onNavigate
}: CompatibilityPageProps) {
  const ctaRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const scrollToCta = () => {
    ctaRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-23039">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 sm:pt-8 pb-14 sm:pb-24 lg:pb-32" data-id="element-23040">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-rose-500/12 rounded-full blur-3xl pointer-events-none" data-id="element-23041" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-pink-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-23042" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-23043">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Услуги',
          onClick: () => onNavigate('marketplace')
        }, {
          label: 'Совместимость'
        }]} className="mb-6 sm:mb-12 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-23044" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center" data-id="element-23045">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-23046">
              <motion.div variants={staggerItem} className="mb-4 sm:mb-5" data-id="element-23047">
                <Badge variant="pro" className="bg-rose-500/20 text-rose-300 border-rose-500/30 px-3 py-1 text-sm" data-id="element-23048">
                  Для пар
                </Badge>
              </motion.div>

              <motion.h1 variants={staggerItem} className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 leading-[1.15]" data-id="element-23049">
                Узнайте астрологическую совместимость в отношениях
              </motion.h1>

              <motion.p variants={staggerItem} className="text-sm sm:text-base md:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed" data-id="element-23050">
                Синастрия — это астрологический анализ двух натальных карт,
                который показывает эмоциональную, психологическую и жизненную
                совместимость людей.
              </motion.p>

              <motion.div variants={staggerItem} className="mb-6 sm:mb-10" data-id="element-23051">
                <Button size="lg" onClick={scrollToCta} className="text-base sm:text-lg px-6 sm:px-8 shadow-lg shadow-rose-500/20 bg-rose-500 hover:bg-rose-600" data-id="element-23052">
                  Проверить совместимость
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-23053" />
                </Button>
              </motion.div>

              <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-23054">
                {[{
                icon: <Heart className="w-4 h-4" data-id="element-23055" />,
                text: 'Анализ отношений'
              }, {
                icon: <Sparkles className="w-4 h-4" data-id="element-23056" />,
                text: 'Сильные стороны пары'
              }, {
                icon: <AlertTriangle className="w-4 h-4" data-id="element-23057" />,
                text: 'Выявление конфликтов'
              }, {
                icon: <Target className="w-4 h-4" data-id="element-23058" />,
                text: 'Рекомендации для гармонии'
              }].map((item, i) => <div key={i} className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-23059">
                    <div className="w-7 h-7 rounded-lg bg-rose-500/20 flex items-center justify-center text-rose-300 flex-shrink-0" data-id="element-23060">
                      {item.icon}
                    </div>
                    {item.text}
                  </div>)}
              </motion.div>
            </motion.div>

            {/* Illustration — two connected charts */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.2
          }} className="hidden lg:flex justify-center relative" data-id="element-23061">
              <div className="relative w-[420px] h-[420px]" data-id="element-23062">
                {/* Person 1 circle */}
                <motion.div className="absolute left-4 top-1/2 -translate-y-1/2 w-[220px] h-[220px] rounded-full border border-rose-400/25" animate={{
                scale: [1, 1.03, 1]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-23063">
                  <div className="absolute inset-0 rounded-full border border-rose-300/10 animate-[spin_50s_linear_infinite]" data-id="element-23064" />
                  <div className="w-28 h-28 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full blur-3xl opacity-20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-23065" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-23066">
                    <Moon className="w-10 h-10 text-rose-300/60" strokeWidth={1} data-id="element-23067" />
                  </div>
                </motion.div>
                {/* Person 2 circle */}
                <motion.div className="absolute right-4 top-1/2 -translate-y-1/2 w-[220px] h-[220px] rounded-full border border-sky-400/25" animate={{
                scale: [1, 1.03, 1]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1
              }} data-id="element-23068">
                  <div className="absolute inset-0 rounded-full border border-sky-300/10 animate-[spin_45s_linear_infinite_reverse]" data-id="element-23069" />
                  <div className="w-28 h-28 bg-gradient-to-br from-sky-400 to-blue-500 rounded-full blur-3xl opacity-20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-23070" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-23071">
                    <Sun className="w-10 h-10 text-sky-300/60" strokeWidth={1} data-id="element-23072" />
                  </div>
                </motion.div>
                {/* Connection heart */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" data-id="element-23073">
                  <motion.div animate={{
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-23074">
                    <Heart className="w-10 h-10 text-rose-400/80 fill-rose-400/30" data-id="element-23075" />
                  </motion.div>
                </div>
                {/* Connection lines */}
                {[30, -30, 60, -60].map((angle, i) => <div key={i} className="absolute top-1/2 left-1/2 w-24 h-px bg-gradient-to-r from-rose-400/20 via-white/10 to-sky-400/20" style={{
                transform: `translate(-50%, -50%) rotate(${angle}deg)`
              }} data-id="element-23076" />)}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 2. ЧТО ТАКОЕ СИНАСТРИЯ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-23077">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23078">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-6 sm:mb-8 text-center" data-id="element-23079">
            Что такое синастрия
          </h2>
          <div className="prose prose-lg prose-slate max-w-none text-slate-600 space-y-6" data-id="element-23080">
            <p data-id="element-23081">
              Синастрия — это астрологический метод анализа отношений между
              двумя людьми. Он основан на сравнении двух натальных карт и
              показывает, как энергии планет одного человека взаимодействуют с
              планетами другого. Это один из самых точных инструментов для
              понимания динамики любых отношений — романтических, дружеских,
              деловых.
            </p>
            <p data-id="element-23082">
              При синастрическом анализе астролог накладывает одну карту на
              другую и изучает <strong data-id="element-23083">аспекты</strong> — углы между планетами
              двух людей. Гармоничные аспекты (тригоны, секстили) показывают
              зоны лёгкости и взаимопонимания, а напряжённые (квадратуры,
              оппозиции) — области, где могут возникать конфликты и
              недопонимание.
            </p>
            <p data-id="element-23084">
              Этот анализ помогает понять{' '}
              <strong data-id="element-23085">эмоциональную совместимость</strong>, психологическую
              динамику отношений, сильные стороны пары и возможные зоны
              напряжения. Зная эти особенности, партнёры могут осознанно
              работать над отношениями и избегать типичных ловушек.
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. ЧТО ПОКАЗЫВАЕТ АНАЛИЗ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23086">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23087">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12" data-id="element-23088">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23089">
              Что показывает анализ совместимости
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-23090">
              Шесть ключевых аспектов ваших отношений
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto" data-id="element-23091">
            {[{
            icon: <Heart className="w-5 h-5" data-id="element-23092" />,
            color: 'bg-rose-50 text-rose-600',
            title: 'Эмоциональная совместимость',
            desc: 'Насколько партнёры понимают чувства и внутренний мир друг друга, способны ли к эмпатии.'
          }, {
            icon: <Sparkles className="w-5 h-5" data-id="element-23093" />,
            color: 'bg-pink-50 text-pink-600',
            title: 'Романтическая совместимость',
            desc: 'Как проявляется притяжение, страсть и эмоциональная близость между партнёрами.'
          }, {
            icon: <MessageCircle className="w-5 h-5" data-id="element-23094" />,
            color: 'bg-sky-50 text-sky-600',
            title: 'Коммуникация',
            desc: 'Насколько легко людям общаться, понимать друг друга и находить компромиссы.'
          }, {
            icon: <TrendingUp className="w-5 h-5" data-id="element-23095" />,
            color: 'bg-emerald-50 text-emerald-600',
            title: 'Долгосрочные перспективы',
            desc: 'Потенциал отношений, их развитие со временем и устойчивость связи.'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-23096" />,
            color: 'bg-amber-50 text-amber-600',
            title: 'Сильные стороны отношений',
            desc: 'Что помогает партнёрам поддерживать гармонию, точки притяжения и ресурсы пары.'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-23097" />,
            color: 'bg-orange-50 text-orange-600',
            title: 'Возможные сложности',
            desc: 'Какие аспекты могут создавать напряжение и как с ними экологично работать.'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="flex items-start gap-4 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-23098">
                <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-23099">
                  {item.icon}
                </div>
                <div className="min-w-0" data-id="element-23100">
                  <h3 className="text-base font-bold text-slate-900 mb-1" data-id="element-23101">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-23102">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 4. КАК РАССЧИТЫВАЕТСЯ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-23103">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23104">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12" data-id="element-23105">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23106">
              Как рассчитывается совместимость
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-23107">
              Пять шагов от данных до готового анализа
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto" data-id="element-23108">
            {[{
            step: '01',
            icon: <User className="w-5 h-5" data-id="element-23109" />,
            color: 'bg-rose-50 text-rose-600',
            stepColor: 'text-rose-500',
            title: 'Данные партнёра 1',
            desc: 'Дата, время и место рождения первого человека'
          }, {
            step: '02',
            icon: <User className="w-5 h-5" data-id="element-23110" />,
            color: 'bg-sky-50 text-sky-600',
            stepColor: 'text-sky-500',
            title: 'Данные партнёра 2',
            desc: 'Дата, время и место рождения второго человека'
          }, {
            step: '03',
            icon: <Star className="w-5 h-5" data-id="element-23111" />,
            color: 'bg-indigo-50 text-indigo-600',
            stepColor: 'text-indigo-500',
            title: 'Две натальные карты',
            desc: 'Система строит карту рождения каждого партнёра'
          }, {
            step: '04',
            icon: <Gem className="w-5 h-5" data-id="element-23112" />,
            color: 'bg-purple-50 text-purple-600',
            stepColor: 'text-purple-500',
            title: 'Анализ аспектов',
            desc: 'Изучаются взаимодействия планет между картами'
          }, {
            step: '05',
            icon: <FileText className="w-5 h-5" data-id="element-23113" />,
            color: 'bg-emerald-50 text-emerald-600',
            stepColor: 'text-emerald-500',
            title: 'Отчёт',
            desc: 'Формируется подробный анализ совместимости'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="flex items-center gap-4 bg-slate-50 rounded-xl px-5 py-4 border border-slate-100" data-id="element-23114">
                <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-23115">
                  {item.icon}
                </div>
                <div className="min-w-0 flex-1" data-id="element-23116">
                  <div className="flex items-center gap-2 mb-0.5" data-id="element-23117">
                    <span className={`text-xs font-bold ${item.stepColor}`} data-id="element-23118">
                      {item.step}
                    </span>
                    <h3 className="text-base font-bold text-slate-900" data-id="element-23119">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-slate-500 text-sm" data-id="element-23120">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 5. ЧТО ВХОДИТ В ОТЧЁТ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23121">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23122">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-23123">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23124">
              Что входит в отчёт совместимости
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-23125">
              Подробный документ с полным анализом взаимодействия двух карт
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto" data-id="element-23126">
            {[{
            icon: <Heart className="w-5 h-5" data-id="element-23127" />,
            text: 'Общий анализ совместимости'
          }, {
            icon: <Sun className="w-5 h-5" data-id="element-23128" />,
            text: 'Взаимодействие Солнца и Луны'
          }, {
            icon: <Sparkles className="w-5 h-5" data-id="element-23129" />,
            text: 'Взаимодействие Венеры и Марса'
          }, {
            icon: <Eye className="w-5 h-5" data-id="element-23130" />,
            text: 'Эмоциональная динамика отношений'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-23131" />,
            text: 'Сильные стороны пары'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-23132" />,
            text: 'Возможные конфликты и их причины'
          }, {
            icon: <Target className="w-5 h-5" data-id="element-23133" />,
            text: 'Рекомендации для гармонии'
          }, {
            icon: <Gem className="w-5 h-5" data-id="element-23134" />,
            text: 'Кармические связи между партнёрами'
          }, {
            icon: <BookOpen className="w-5 h-5" data-id="element-23135" />,
            text: 'Таблица аспектов между картами'
          }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-23136">
                <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0" data-id="element-23137">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-23138">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 6. ПРИМЕР ОТЧЁТА */}
      <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-23139">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23140">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl" data-id="element-23141">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-23142">
              <div className="p-6 sm:p-10 lg:p-16 flex flex-col justify-center" data-id="element-23143">
                <span className="text-xs font-bold text-rose-300 uppercase tracking-[0.2em] mb-3 sm:mb-4" data-id="element-23144">
                  Пример отчёта
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight" data-id="element-23145">
                  Глубокий анализ отношений, а не общие фразы
                </h2>
                <p className="text-slate-400 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed" data-id="element-23146">
                  Каждый отчёт содержит две натальные карты, таблицу аспектов
                  между партнёрами и подробный анализ каждой сферы отношений.
                </p>
                <ul className="space-y-3 mb-6 sm:mb-10" data-id="element-23147">
                  {['Графические схемы двух натальных карт', 'Таблица аспектов между партнёрами', 'Персональный анализ отношений', 'Практические рекомендации для пары'].map((t, i) => <li key={i} className="flex items-center text-slate-300 text-sm" data-id="element-23148">
                      <Check className="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" data-id="element-23149" />
                      {t}
                    </li>)}
                </ul>
                <Button size="lg" variant="outline" className="w-fit bg-white border-0 hover:bg-slate-100" onClick={() => onNavigate('report-preview-compatibility')} data-id="element-23150">
                  Посмотреть пример PDF
                </Button>
              </div>
              <div className="bg-slate-800/50 p-4 sm:p-8 lg:p-12 flex items-center justify-center" data-id="element-23151">
                <div className="relative w-full max-w-xs" data-id="element-23152">
                  {/* Card 1 */}
                  <motion.div animate={{
                  y: [0, -6, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="bg-white rounded-xl shadow-2xl p-6 relative z-10" data-id="element-23153">
                    <div className="flex gap-3 mb-5" data-id="element-23154">
                      <div className="flex-1 h-16 bg-gradient-to-br from-rose-50 to-pink-50 rounded-lg flex items-center justify-center" data-id="element-23155">
                        <div className="w-10 h-10 rounded-full border-2 border-rose-200 flex items-center justify-center" data-id="element-23156">
                          <Moon className="w-4 h-4 text-rose-300" data-id="element-23157" />
                        </div>
                      </div>
                      <div className="flex-1 h-16 bg-gradient-to-br from-sky-50 to-blue-50 rounded-lg flex items-center justify-center" data-id="element-23158">
                        <div className="w-10 h-10 rounded-full border-2 border-sky-200 flex items-center justify-center" data-id="element-23159">
                          <Sun className="w-4 h-4 text-sky-400" data-id="element-23160" />
                        </div>
                      </div>
                    </div>
                    {/* Compatibility bars */}
                    <div className="space-y-3 mb-5" data-id="element-23161">
                      {[{
                      label: 'Эмоции',
                      pct: 85,
                      color: 'bg-rose-400'
                    }, {
                      label: 'Романтика',
                      pct: 92,
                      color: 'bg-pink-400'
                    }, {
                      label: 'Общение',
                      pct: 78,
                      color: 'bg-sky-400'
                    }, {
                      label: 'Перспективы',
                      pct: 88,
                      color: 'bg-emerald-400'
                    }].map((bar, i) => <div key={i} data-id="element-23162">
                          <div className="flex justify-between text-[10px] text-slate-400 mb-1" data-id="element-23163">
                            <span data-id="element-23164">{bar.label}</span>
                            <span data-id="element-23165">{bar.pct}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-23166">
                            <motion.div className={`h-full rounded-full ${bar.color}`} initial={{
                          width: 0
                        }} whileInView={{
                          width: `${bar.pct}%`
                        }} viewport={{
                          once: true
                        }} transition={{
                          duration: 1,
                          delay: 0.3 + i * 0.15,
                          ease: 'easeOut'
                        }} data-id="element-23167" />
                          </div>
                        </div>)}
                    </div>
                    <div className="space-y-2" data-id="element-23168">
                      <div className="w-full h-1.5 bg-slate-100 rounded" data-id="element-23169" />
                      <div className="w-full h-1.5 bg-slate-100 rounded" data-id="element-23170" />
                      <div className="w-3/4 h-1.5 bg-slate-50 rounded" data-id="element-23171" />
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 7. ДЛЯ КОГО */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23172">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23173">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-23174">
            Для кого подходит анализ совместимости
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-23175">
            {[{
            icon: <Heart className="w-5 h-5" data-id="element-23176" />,
            text: 'Людям в отношениях, которые хотят укрепить связь'
          }, {
            icon: <Sparkles className="w-5 h-5" data-id="element-23177" />,
            text: 'Тем, кто только начинает отношения и хочет понять перспективы'
          }, {
            icon: <Eye className="w-5 h-5" data-id="element-23178" />,
            text: 'Тем, кто хочет лучше понять своего партнёра'
          }, {
            icon: <Target className="w-5 h-5" data-id="element-23179" />,
            text: 'Тем, кто хочет улучшить отношения и избежать конфликтов'
          }, {
            icon: <Users className="w-5 h-5" data-id="element-23180" />,
            text: 'Друзьям и деловым партнёрам для понимания динамики'
          }, {
            icon: <Lightbulb className="w-5 h-5" data-id="element-23181" />,
            text: 'Родителям, которые хотят лучше понять ребёнка'
          }].map((item, i) => <div key={i} className="flex items-center gap-4 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-23182">
                <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0" data-id="element-23183">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-23184">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 8. ПОЧЕМУ АСТРОЛОГИЯ ПОМОГАЕТ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-23185">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23186">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-23187">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23188">
              Почему астрология помогает понять отношения
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-23189">
              Астрологический анализ помогает увидеть глубинную динамику
              отношений и понять, какие энергии взаимодействуют между двумя
              людьми.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto" data-id="element-23190">
            {[{
            icon: <Eye className="w-6 h-6" data-id="element-23191" />,
            color: 'bg-rose-100 text-rose-600',
            title: 'Лучше понимать партнёра',
            desc: 'Вы увидите мотивации, потребности и страхи партнёра через призму его натальной карты — не домыслы, а астрологические факты.'
          }, {
            icon: <Shield className="w-6 h-6" data-id="element-23192" />,
            color: 'bg-sky-100 text-sky-600',
            title: 'Избегать конфликтов',
            desc: 'Зная зоны напряжения заранее, вы сможете осознанно работать с ними, а не наступать на одни и те же грабли.'
          }, {
            icon: <Heart className="w-6 h-6" data-id="element-23193" />,
            color: 'bg-emerald-100 text-emerald-600',
            title: 'Строить гармонию',
            desc: 'Понимание сильных сторон пары помогает опираться на них и создавать по-настоящему крепкие отношения.'
          }].map((item, i) => <Card key={i} padding="lg" className="border-slate-100 text-center" data-id="element-23194">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${item.color} flex items-center justify-center mx-auto mb-4 sm:mb-5`} data-id="element-23195">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-23196">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-23197">
                  {item.desc}
                </p>
              </Card>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 9. CTA */}
      <div ref={ctaRef} className="scroll-mt-10" data-id="element-23198">
        <AnimatedSection className="py-14 sm:py-24 bg-slate-900" data-id="element-23199">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-23200">
            <motion.div animate={{
            scale: [1, 1.15, 1]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-23201">
              <Heart className="w-10 h-10 text-rose-400 mx-auto mb-6 fill-rose-400/20" data-id="element-23202" />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6" data-id="element-23203">
              Проверьте астрологическую совместимость
            </h2>
            <p className="text-base sm:text-lg text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto" data-id="element-23204">
              Введите данные рождения двух людей, чтобы получить персональный
              анализ отношений и рекомендации для гармоничной пары.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-23205">
              <Button size="lg" onClick={scrollToPricing} className="text-base sm:text-lg px-10 shadow-lg shadow-rose-500/20 bg-rose-500 hover:bg-rose-600" data-id="element-23206">
                Рассчитать совместимость
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-23207" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500" data-id="element-23208">
              <span className="flex items-center" data-id="element-23209">
                <Shield className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-23210" /> Данные
                защищены
              </span>
              <span className="flex items-center" data-id="element-23211">
                <Clock className="w-4 h-4 mr-1.5 text-rose-400" data-id="element-23212" /> Результат
                мгновенно
              </span>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* 10. ТАРИФЫ */}
      <div ref={pricingRef} className="scroll-mt-10" data-id="element-23213">
        <ServicePurchaseSection serviceType="compatibility" onNavigate={onNavigate} data-id="element-23214" />
      </div>

      {/* Ad Banner */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-23215">
        <AdSlot slotId="SLOT-10" onNavigate={onNavigate} data-id="element-23216" />
      </div>

      {/* FAQ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23217">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23218">
          <div className="text-center mb-8 sm:mb-12" data-id="element-23219">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23220">
              Частые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Нужно ли знать точное время рождения обоих партнёров?',
          content: 'Для максимально точного анализа желательно знать время рождения обоих людей. Время влияет на положение Асцендента и домов, что важно для анализа совместимости. Если время одного из партнёров неизвестно, анализ всё равно возможен, но некоторые аспекты будут менее точными.'
        }, {
          id: '2',
          title: 'Можно ли анализировать не только романтические отношения?',
          content: 'Да! Синастрия подходит для анализа любых отношений — романтических, дружеских, деловых, родительско-детских. Метод одинаково точно показывает динамику взаимодействия между любыми двумя людьми.'
        }, {
          id: '3',
          title: 'Что если анализ покажет плохую совместимость?',
          content: 'Не бывает «идеальной» или «плохой» совместимости. У каждой пары есть сильные стороны и зоны напряжения. Отчёт показывает обе стороны и даёт практические рекомендации, как работать со сложными аспектами. Осознанность — ключ к гармоничным отношениям.'
        }, {
          id: '4',
          title: 'Чем базовый анализ отличается от Premium?',
          content: 'Базовый анализ охватывает ключевые аспекты совместимости (Солнце, Луна, основные взаимодействия) — 1-3 страницы. Premium включает полный разбор всех планетарных взаимодействий, кармические связи, сексуальную совместимость и детальные рекомендации — 30-40 страниц.'
        }, {
          id: '5',
          title: 'Как быстро я получу отчёт?',
          content: 'Мгновенно. Сразу после оплаты система сгенерирует PDF-файл с анализом совместимости. Вы сможете скачать его на странице успеха, также он будет отправлен на email.'
        }]} data-id="element-23221" />
        </div>
      </AnimatedSection>
    </div>;
}